import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { theme, combineClasses } from '../theme/theme';

/**
 * ============================================================================
 * 组件模块：components/Modal.jsx
 * 
 * 说明：
 * - 这是一个通用的模态框组件，可以在页面内弹出显示详细信息
 * - 支持点击背景关闭、ESC键关闭
 * - 适用于团队成员详情、新闻详情等场景
 * 
 * 使用方法：
 * <Modal isOpen={isOpen} onClose={handleClose} title="标题" themeMode="dark">
 *   <p>内容</p>
 * </Modal>
 * 
 * Props:
 * - isOpen: boolean - 控制模态框显示/隐藏
 * - onClose: function - 关闭模态框的回调函数
 * - title: string - 模态框标题（可选）
 * - subtitle: string - 标题下方小字（可选），如 contact.subtitle
 * - titleSize: 'md' | 'lg' - 标题字号，'lg' 约为默认的两倍（可选，默认'md'）
 * - children: ReactNode - 模态框内容
 * - size: 'sm' | 'md' | 'lg' | 'xl' - 模态框大小（默认'md'）
 * - themeMode: 'light' | 'dark' - 主题模式（默认'dark'）
 * ============================================================================
 */
const Modal = ({ isOpen, onClose, title, subtitle, titleSize = 'md', children, size = 'md', themeMode = 'dark' }) => {
  const isLight = themeMode === 'light';
  // ESC键关闭模态框
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // 防止背景滚动
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // 定义模态框大小
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl'
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* 背景遮罩层 */}
      <div className={combineClasses(
        "absolute inset-0 backdrop-blur-sm animate-fade-in",
        isLight ? "bg-gray-900/50" : "bg-black/80"
      )} />
      
      {/* 模态框内容 */}
      <div
        className={combineClasses(
          "relative w-full",
          sizeClasses[size],
          "border rounded-2xl",
          "shadow-2xl overflow-hidden",
          "animate-scale-in",
          "max-h-[80vh] flex flex-col",
          isLight 
            ? "bg-white border-gray-200" 
            : "bg-gradient-to-br from-gray-900 to-black border-white/20"
        )}
        onClick={(e) => e.stopPropagation()} // 阻止点击内容时关闭
        style={{ marginTop: '100px' }} // 为导航栏留出空间
      >
        {/* 头部：标题（可 titleSize 放大）+ 可选 subtitle 小字 + 关闭按钮 */}
        {title && (
          <div className={combineClasses(
            "flex items-center justify-between gap-4 p-6 border-b",
            isLight ? "border-gray-200" : "border-white/10"
          )}>
            <div className="min-w-0 flex-1">
              <h2 className={combineClasses(
                "font-bold",
                titleSize === 'lg' ? "text-5xl" : "text-2xl",
                isLight ? "text-gray-900" : "text-white"
              )}>{title}</h2>
              {subtitle && (
                <p className={combineClasses(
                  "mt-1 text-sm",
                  isLight ? "text-gray-500" : "text-gray-400"
                )}>{subtitle}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className={combineClasses(
                "p-2 rounded-full transition-all",
                isLight 
                  ? "hover:bg-gray-100 text-gray-600 hover:text-gray-900" 
                  : "hover:bg-white/10 text-gray-400 hover:text-white"
              )}
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        )}

        {/* 如果没有标题，显示右上角关闭按钮 */}
        {!title && (
          <button
            onClick={onClose}
            className={combineClasses(
              "absolute top-4 right-4 z-10 p-2 rounded-full transition-all",
              isLight 
                ? "hover:bg-gray-100 text-gray-600 hover:text-gray-900" 
                : "hover:bg-white/10 text-gray-400 hover:text-white"
            )}
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        )}

        {/* 内容区域 */}
        <div className="overflow-y-auto flex-1 p-6">
          {children}
        </div>
      </div>

      {/* 动画样式 */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }

        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Modal;
